import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CloudRain, RefreshCw } from "lucide-react";
import {
  fetchCityWeather,
  WeatherData,
  WeatherForecastDay,
  WeatherCurrent,
} from "../services/weatherService";
import WeatherIcon from "./WeatherIcon";

interface CityWeatherPanelProps {
  cityId: number;
  cityName: string;
  coordinatesAvailable?: boolean;
  className?: string;
}

const persianNumber = (value: number, maximumFractionDigits = 0) =>
  new Intl.NumberFormat("fa-IR", { maximumFractionDigits }).format(value);

const formatForecastDate = (date: string) =>
  new Intl.DateTimeFormat("fa-IR", { weekday: "short", day: "numeric", month: "short" }).format(
    new Date(`${date}T12:00:00`),
  );

const formatUpdatedAt = (value?: string) => {
  if (!value) return null;
  return new Intl.DateTimeFormat("fa-IR", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const ForecastCard: React.FC<{ day: WeatherForecastDay }> = ({ day }) => {
  const precipitation = Math.round((day.precipitation_probability_max || 0) * 100);

  return (
    <article className="min-w-[138px] rounded-xl border border-gray-200 bg-white p-3 text-center tablet:min-w-0">
      <p className="text-xs text-gray-600 font-myYekanFaNumMedium">{formatForecastDate(day.date)}</p>
      <div className="mt-2 flex items-center justify-center gap-2">
        <WeatherIcon condition={day.condition} className="h-10 w-10" loading="lazy" />
        <div>
          <p className="text-sm text-gray-900 font-myYekanFaNumDemiBold">
            {persianNumber(day.temperature_max_c)}° / {persianNumber(day.temperature_min_c)}°
          </p>
          <p className="mt-0.5 line-clamp-1 text-[10px] text-gray-500">{day.condition.description}</p>
        </div>
      </div>
      {/* <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-500">
        <CloudRain size={13} className="text-[#038545]" /> احتمال بارش {persianNumber(precipitation)}٪
      </div> */}
    </article>
  );
};

const CurrentWeatherCard: React.FC<{ current: WeatherCurrent }> = ({ current }) => (
  <article className="min-w-[138px] rounded-xl border border-[#97d2b5] bg-[#f3fefa] p-3 text-center tablet:min-w-0">
    <p className="text-xs font-myYekanFaNumMedium text-[#068646]">امروز</p>
    <div className="mt-2 flex items-center justify-center gap-2">
      <WeatherIcon condition={current.condition} className="h-10 w-10" />
      <div>
        <p className="text-sm font-myYekanFaNumDemiBold text-gray-900">{persianNumber(current.temperature_c)}°</p>
        <p className="mt-0.5 line-clamp-1 text-[10px] text-gray-500">{current.condition.description}</p>
      </div>
    </div>
  </article>
);

const CityWeatherPanel: React.FC<CityWeatherPanelProps> = ({ cityId, cityName, coordinatesAvailable = true, className = "" }) => {
  const { data, isFetching } = useQuery<WeatherData, Error>({
    queryKey: ["weather", "city", cityId, 5],
    queryFn: ({ signal }) => fetchCityWeather(cityId, 5, signal),
    staleTime: 15 * 60 * 1000,
    retry: false,
    enabled: coordinatesAvailable,
  });

  const current = data?.current;
  const forecastDays = data?.forecast?.days || [];
  if (!coordinatesAvailable || !data || (!current && forecastDays.length === 0)) return null;
  const cacheStates = [data.source.current, data.source.forecast].filter(Boolean);
  const isStale = cacheStates.some((state) => state?.stale);
  const updatedAt = formatUpdatedAt(data.source.current?.fetched_at || data.source.forecast?.fetched_at);

  return (
    <section className={`site-container mt-8 ${className}`} aria-labelledby="city-weather-title">
      <div className="rounded-xl border border-gray-200 bg-[#fafafa] p-4 tablet:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {/* <span className="text-xs text-[#038545]">پیش‌بینی مقصد</span> */}
            <h2 id="city-weather-title" className="mt-1 text-lg font-myYekanDemibold text-gray-900 tablet:text-xl">آب‌وهوای {cityName}</h2>
          </div>
          {isFetching && <RefreshCw size={17} className="animate-spin text-gray-400" aria-label="در حال بروزرسانی" />}
        </div>

        {current ? (
          <div className="hidden">
            <div className="flex items-center gap-3">
              <WeatherIcon condition={current.condition} className="h-16 w-16" />
              <div>
                {/* <p className="text-3xl text-gray-950 font-myYekanFaNumDemiBold">{persianNumber(current.temperature_c, 1)}°</p> */}
                <p className="text-3xl text-gray-950 font-myYekanFaNumDemiBold">{persianNumber(current.temperature_c)}°</p>
                <p className="mt-1 text-sm text-gray-600">{current.condition.description}</p>
              </div>
            </div>
            {/* <div className="grid grid-cols-3 gap-3 text-xs text-gray-600 tablet:min-w-[360px]">
              <div className="rounded-lg bg-white p-3"><ThermometerSun size={16} className="mb-2 text-[#038545]" /><span className="block text-[10px] text-gray-400">دمای احساسی</span><b className="mt-1 block font-myYekanFaNumMedium">{persianNumber(current.feels_like_c, 1)}°</b></div>
              <div className="rounded-lg bg-white p-3"><Droplets size={16} className="mb-2 text-[#038545]" /><span className="block text-[10px] text-gray-400">رطوبت</span><b className="mt-1 block font-myYekanFaNumMedium">{persianNumber(current.humidity_percent)}٪</b></div>
              <div className="rounded-lg bg-white p-3"><CloudRain size={16} className="mb-2 text-[#038545]" /><span className="block text-[10px] text-gray-400">بارش یک ساعت</span><b className="mt-1 block font-myYekanFaNumMedium">{persianNumber(current.rain_mm_1h || 0, 1)} میلی‌متر</b></div>
            </div> */}
          </div>
        ) : (
          <p className="mt-4 rounded-xl bg-gray-100 p-3 text-xs text-gray-600">هوای فعلی در دسترس نیست؛ پیش‌بینی روزهای آینده را ببینید.</p>
        )}

        {(current || forecastDays.length > 0) ? (
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2 tablet:grid tablet:grid-cols-6 tablet:overflow-visible">
            {current && <CurrentWeatherCard current={current} />}
            {forecastDays.slice(0, 6).map((day) => <ForecastCard key={day.date} day={day} />)}
          </div>
        ) : (
          <p className="mt-4 rounded-xl border border-dashed border-gray-300 p-4 text-xs text-gray-600">پیش‌بینی روزهای آینده فعلاً در دسترس نیست.</p>
        )}

        {!!data.source.warnings?.length && (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-6 text-amber-800">
            {data.source.warnings.map((warning, index) => <p key={`${warning}-${index}`}>{warning}</p>)}
          </div>
        )}

        {(isStale || updatedAt) && (
          <div className="mt-4 border-t border-gray-200 pt-3 text-[10px] text-gray-500">
            <span>{isStale ? "آخرین اطلاعات ذخیره‌شده" : "آخرین بروزرسانی"}{updatedAt ? `: ${updatedAt}` : ""}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default CityWeatherPanel;
